import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Briefcase, 
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb,
  Target,
  Sparkles
} from 'lucide-react'
import { useExecutiveSummary, usePredictiveAnalysis, useMonthlyComparisonReport } from '@/hooks/useReportsEngine'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { PredictiveAnalysisCard } from './PredictiveAnalysisCard'

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })

export function ExecutiveReportTab() {
  const { data: executive, isLoading: loadingExecutive } = useExecutiveSummary()
  const { data: predictive, isLoading: loadingPredictive } = usePredictiveAnalysis()
  const { data: comparison, isLoading: loadingComparison } = useMonthlyComparisonReport()

  const isLoading = loadingExecutive || loadingPredictive || loadingComparison

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-[200px] rounded-xl" />
          <Skeleton className="h-[200px] rounded-xl" />
        </div>
        <Skeleton className="h-[300px] rounded-xl" />
      </div>
    )
  }

  const isPositiveGrowth = (executive?.growthPercent || 0) >= 0

  return (
    <div className="space-y-6">
      {/* Main metrics */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Current month performance */}
        <Card className="shadow-card bg-gradient-to-br from-primary/5 via-transparent to-transparent border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              Performance do Mês
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center p-4 bg-card rounded-xl border">
              <p className="text-sm text-muted-foreground mb-1">Receita Atual</p>
              <p className="text-4xl font-display font-bold text-primary">
                {formatCurrency(executive?.currentMonthRevenue || 0)}
              </p>
              <div className={cn(
                "inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-sm font-medium",
                isPositiveGrowth ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
              )}>
                {isPositiveGrowth ? (
                  <ArrowUpRight className="h-4 w-4" />
                ) : (
                  <ArrowDownRight className="h-4 w-4" />
                )}
                {isPositiveGrowth ? '+' : ''}{executive?.growthPercent || 0}% vs mês anterior
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-muted/50 rounded-xl">
                <p className="text-xs text-muted-foreground">Mês Anterior</p>
                <p className="text-lg font-semibold">{formatCurrency(executive?.previousMonthRevenue || 0)}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-xl">
                <p className="text-xs text-muted-foreground">Valor/Hora Real</p>
                <p className="text-lg font-semibold">{formatCurrency(executive?.realHourlyRate || 0)}/h</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Predictive analysis */}
        {predictive && (
          <PredictiveAnalysisCard
            avgMonthlyRevenue={predictive.avgMonthlyRevenue}
            growthTrend={predictive.growthTrend}
            projectedNextMonth={predictive.projectedNextMonth}
            confidence={predictive.confidence}
            monthsAnalyzed={predictive.monthsAnalyzed}
          />
        )}
      </div>

      {/* Top performers */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Top client */}
        {executive?.topClient && (
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-warning" />
                Cliente Destaque
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-gradient-to-br from-warning/10 to-warning/5 border border-warning/20 rounded-xl">
                <p className="text-lg font-semibold">{executive.topClient.name}</p>
                <p className="text-2xl font-bold text-warning mt-1">
                  {formatCurrency(executive.topClient.revenue)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Maior receita do mês
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top project */}
        {executive?.topProject && (
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-success" />
                Projeto Destaque
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-gradient-to-br from-success/10 to-success/5 border border-success/20 rounded-xl">
                <p className="text-lg font-semibold">{executive.topProject.name}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Receita</p>
                    <p className="text-xl font-bold text-success">
                      {formatCurrency(executive.topProject.revenue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Taxa/h</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(executive.topProject.rate)}/h
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Insights */}
      {executive?.insights && executive.insights.length > 0 && (
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-warning" />
              Insights Automáticos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {executive.insights.map((insight, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 bg-muted/50 rounded-xl"
                >
                  <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm">{insight}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison summary */}
      {comparison && (
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Comparativo com Mês Anterior
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <DollarSign className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Receita</p>
                <div className={cn(
                  "text-xl font-bold flex items-center justify-center gap-1",
                  comparison.revenueGrowth >= 0 ? "text-success" : "text-destructive"
                )}>
                  {comparison.revenueGrowth >= 0 ? (
                    <ArrowUpRight className="h-5 w-5" />
                  ) : (
                    <ArrowDownRight className="h-5 w-5" />
                  )}
                  {comparison.revenueGrowth >= 0 ? '+' : ''}{comparison.revenueGrowth}%
                </div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <TrendingUp className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Horas</p>
                <div className={cn(
                  "text-xl font-bold flex items-center justify-center gap-1",
                  comparison.hoursGrowth >= 0 ? "text-success" : "text-destructive"
                )}>
                  {comparison.hoursGrowth >= 0 ? (
                    <ArrowUpRight className="h-5 w-5" />
                  ) : (
                    <ArrowDownRight className="h-5 w-5" />
                  )}
                  {comparison.hoursGrowth >= 0 ? '+' : ''}{comparison.hoursGrowth}%
                </div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-xl">
                <Briefcase className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Projetos</p>
                <p className="text-xl font-bold">
                  {comparison.currentProjects}
                  <span className="text-sm text-muted-foreground font-normal ml-1">
                    (ant: {comparison.previousProjects})
                  </span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
